import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable, Field, PageHeader } from "@/components/common";
import Modal from "@/components/Modal";

const EMPTY = { code: "", name: "", siret: "", vat_number: "", address: "", fiscal_year_end: "12-31", invoice_mailbox: "", first_service: "Général" };

function validate(f) {
  const e = {};
  if (!/^[A-Za-z0-9]{2,6}$/.test(f.code.trim())) e.code = "2 à 6 lettres ou chiffres (ex. ODC)";
  if (!f.name.trim()) e.name = "La raison sociale est obligatoire";
  if (f.siret && !/^\d{14}$/.test(f.siret.replace(/\s/g, ""))) e.siret = "Le SIRET doit comporter exactement 14 chiffres";
  if (!/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(f.fiscal_year_end)) e.fiscal_year_end = "Format MM-JJ (ex. 12-31)";
  if (f.invoice_mailbox && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.invoice_mailbox)) e.invoice_mailbox = "Adresse email invalide";
  return e;
}

function CompanyForm({ initial, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const editing = !!initial.code;
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const body = { ...f, code: f.code.trim().toUpperCase(), siret: f.siret.replace(/\s/g, "") };
      editing ? await api.put(`/companies/${initial.code}`, body) : await api.post("/companies", body);
      toast.success(editing ? "Société mise à jour" : "Société créée");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const inp = (k, label, props = {}) => <Field label={label} error={errors[k]}><input className="inp" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`company-${k}-input`} {...props} /></Field>;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="company-form">
      <div className="grid gap-4 sm:grid-cols-2">
        {inp("code", "Préfixe des documents *", { disabled: editing, placeholder: "ex. ODC", className: "inp uppercase" })}
        {inp("name", "Raison sociale *")}
        {inp("siret", "SIRET (14 chiffres)", { inputMode: "numeric" })}
        {inp("vat_number", "N° TVA intracommunautaire")}
        <div className="sm:col-span-2">{inp("address", "Adresse")}</div>
        {inp("fiscal_year_end", "Clôture de l'exercice (MM-JJ)")}
        {inp("invoice_mailbox", "Boîte de réception des factures")}
        {!editing && <div className="sm:col-span-2">{inp("first_service", "Premier service")}<p className="mt-1 text-xs text-[#6B6B80]">Vous serez rattaché à ce service pour créer aussitôt commandes et factures dans cette société.</p></div>}
      </div>
      {editing && <p className="text-xs text-[#6B6B80]">Le préfixe ne peut pas être modifié : il figure dans la numérotation des documents déjà émis.</p>}
      <div className="flex justify-end"><button className="btn-primary" disabled={busy} data-testid="company-save-btn">Enregistrer</button></div>
    </form>
  );
}

export default function Companies() {
  const { loadSettings, refreshUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/companies").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const saved = async () => { setForm(null); await refreshUser(); loadSettings(); load(); };
  const columns = [
    { key: "code", label: "Préfixe", render: (r) => <span className="mono font-semibold">{r.code}</span> },
    { key: "name", label: "Raison sociale", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "siret", label: "SIRET", render: (r) => <span className="mono text-xs">{r.siret}</span> },
    { key: "vat_number", label: "TVA intracom." },
    { key: "address", label: "Adresse" },
    { key: "fiscal_year_end", label: "Clôture" },
    { key: "invoice_mailbox", label: "Boîte factures" },
  ];
  return (
    <div>
      <PageHeader title="Sociétés" subtitle="Les entités du groupe auxquelles vous êtes rattaché. Cliquez sur une ligne pour la modifier.">
        <button className="btn-primary" onClick={() => setForm({})} data-testid="new-company-btn"><Plus size={16} /> Nouvelle société</button>
      </PageHeader>
      <DataTable columns={columns} rows={rows} title="Sociétés" testid="companies-table" onRowClick={(r) => setForm(r)} />
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.code ? `Modifier ${form.name}` : "Nouvelle société"} wide testid="company-modal">
        {form && <CompanyForm key={form.code || "new"} initial={form} onSaved={saved} />}
      </Modal>
    </div>
  );
}
